// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { MainNavigationStyles, MappedLink } from "@insite/content-library/Widgets/Header/MainNavigation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import { styledScrollbar } from "@rc/App/SharedStyles/sharedStyles";
import Tooltip from "@rc/MobiusOverrides/Tooltip/Tooltip";
import * as React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement>;
    isOpen?: boolean;
    displayChangeCustomerLink: boolean;
    setShowDropShadow: ({ showDropShadow }: { showDropShadow: boolean; linkUrl: string }) => void;
    openedNavigationLinkUrl?: string;
}

type Props = ThemeProps<BaseTheme> & OwnProps;

type State = {
    isNavOpen: boolean;
};
class MainNavigationItem extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isNavOpen: false,
        };
    }

    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    closePopover = () => {
        hideAll();
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    render() {
        const { styles, link, index, isOpen, setShowDropShadow, openedNavigationLinkUrl } = this.props;
        const { isNavOpen } = this.state;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading = hasChildren && link.childrenType === "CascadingMenu";
        const isMega = hasChildren && link.childrenType === "MegaMenu";
        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {isCascading && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    className={`menu-item ${openedNavigationLinkUrl === link.url && isNavOpen ? "opened" : ""}`}
                    href={link.url}
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            menuItem = (
                <Menu
                    className="cascading-menu"
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
            );
        }

        if (isMega) {
            const categoryItems = (
                <GridContainer
                    className="mega-menu-grid-container"
                    data-test-selector={`mainNavigationItem_${index}`}
                    {...styles.megaMenuGridContainer}
                    offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                >
                    {link
                        .children!.filter(child => !child.excludeFromNavigation)
                        .map((child, index) => (
                            <GridItem
                                className="mega-menu-grid-item"
                                width={(12 / link.numberOfColumns!) as GridWidths}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                {...styles.megaMenuGridItem}
                                as="li"
                            >
                                <Link
                                    className="mega-menu-heading-link"
                                    {...styles.megaMenuHeading}
                                    href={child.url}
                                    onClick={this.closePopover}
                                >
                                    {child.title}
                                </Link>
                                {child.children && (
                                    <ul data-test-selector={`linksFor_${child.title}`}>
                                        {child.children
                                            .filter(grandChild => {
                                                if (grandChild.excludeFromNavigation) {
                                                    return false;
                                                }
                                                if (
                                                    grandChild.type === "ChangeCustomerPage" &&
                                                    !this.props.displayChangeCustomerLink
                                                ) {
                                                    return false;
                                                }
                                                if (grandChild.type === "VmiUsersPage") {
                                                    const currentMode = getCookie(NavigationModeCookieName);
                                                    if (!currentMode || currentMode === "Storefront") {
                                                        return false;
                                                    }
                                                }

                                                return true;
                                            })
                                            .map((grandChild, index) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <li key={index}>
                                                    <Link
                                                        className="mega-menu-link"
                                                        {...styles.megaMenuLink}
                                                        onClick={this.closePopover}
                                                        href={grandChild.url}
                                                        data-test-selector={grandChild.title}
                                                    >
                                                        {grandChild.title}
                                                    </Link>
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </GridItem>
                        ))}
                </GridContainer>
            );
            menuItem = (
                <Tooltip
                    showFancyScroll={true}
                    trigger="mouseenter focus"
                    className="mega-menu"
                    onShow={() => {
                        hideAll();
                        this.setState({ isNavOpen: true });
                        setTimeout(() => setShowDropShadow({ showDropShadow: true, linkUrl: link.url }));
                    }}
                    animateFill={true}
                    onHidden={() => {
                        this.setState({ isNavOpen: false });
                        setTimeout(() => setShowDropShadow({ showDropShadow: false, linkUrl: link.url }));
                    }}
                    passAsChildren={true}
                    triggerComponent={menuItem}
                    delay={[100, 300]}
                    cssOverrides={{
                        tooltipContainer: css`
                            max-width: 100%;
                            padding: 0;
                            box-shadow: none;
                            border-radius: 0;
                            border: 0;
                            overflow: hidden;
                            overflow-y: scroll;
                            height: auto;
                            max-height: 528px;
                            padding: 16px 0 20px;
                            ${styledScrollbar(3)}
                            & .tippy-arrow {
                                display: none;
                            }
                        `,
                    }}
                >
                    {categoryItems}
                </Tooltip>
            );
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);
